import type { PrometheusQuery } from '../api/types';

function rateInterval(preset: string): string {
  switch (preset) {
    case '1h': return '1m';
    case '6h': return '5m';
    case '24h': return '15m';
    case '7d': return '1h';
    default: return '5m';
  }
}

/** Metadata for the 4 HTTP Traffic panels */
export const HTTP_PANELS = {
  requestRate: {
    title: 'HTTP Request Rate by Service',
    unit: 'reqps',
    refId: 'httpRate',
  },
  latency: {
    title: 'HTTP Request Latency (p50 / p95 / p99)',
    unit: 's',
    refIds: { p50: 'httpP50', p95: 'httpP95', p99: 'httpP99' },
  },
  errorRate: {
    title: 'HTTP Error Rate by Service (5xx)',
    unit: 'percentunit',
    refId: 'httpErr',
    thresholdLine: { value: 0.05, color: '#FF462D' },
  },
  statusCodes: {
    title: 'HTTP Requests by Status Code',
    refId: 'httpStatus',
    legendFormat: 'HTTP {{ http_response_status_code }}',
  },
} as const;

/**
 * Build the PromQL queries for the 4 HTTP Traffic panels.
 */
export function buildHttpTrafficQueries(
  service: string,
  preset: string,
): PrometheusQuery[] {
  const ri = rateInterval(preset);
  const f = `service_name=~"${service}"`;

  return [
    // Panel 1: Request Rate by service (timeseries)
    {
      refId: HTTP_PANELS.requestRate.refId,
      expr: `sum by (service_name) (rate(http_server_request_duration_seconds_count{${f}}[${ri}]))`,
      legendFormat: '{{ service_name }}',
    },
    // Panel 2: Latency p50/p95/p99 by service (timeseries, 3 queries)
    {
      refId: HTTP_PANELS.latency.refIds.p50,
      expr: `histogram_quantile(0.50, sum(rate(http_server_request_duration_seconds_bucket{${f}}[${ri}])) by (le, service_name))`,
      legendFormat: 'p50 — {{ service_name }}',
    },
    {
      refId: HTTP_PANELS.latency.refIds.p95,
      expr: `histogram_quantile(0.95, sum(rate(http_server_request_duration_seconds_bucket{${f}}[${ri}])) by (le, service_name))`,
      legendFormat: 'p95 — {{ service_name }}',
    },
    {
      refId: HTTP_PANELS.latency.refIds.p99,
      expr: `histogram_quantile(0.99, sum(rate(http_server_request_duration_seconds_bucket{${f}}[${ri}])) by (le, service_name))`,
      legendFormat: 'p99 — {{ service_name }}',
    },
    // Panel 3: Error Rate by service (timeseries)
    {
      refId: HTTP_PANELS.errorRate.refId,
      expr: `sum by (service_name) (rate(http_server_request_duration_seconds_count{${f}, http_response_status_code=~"5.."}[${ri}])) / sum by (service_name) (rate(http_server_request_duration_seconds_count{${f}}[${ri}]))`,
      legendFormat: '{{ service_name }}',
    },
    // Panel 4: Status code distribution (pie/donut — uses increase over range)
    {
      refId: HTTP_PANELS.statusCodes.refId,
      expr: `sum by (http_response_status_code) (increase(http_server_request_duration_seconds_count{${f}}[${preset === '1h' ? '1h' : preset === '6h' ? '6h' : preset === '24h' ? '24h' : '7d'}]))`,
      legendFormat: HTTP_PANELS.statusCodes.legendFormat,
    },
  ];
}
